import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

const MODAL_DETAIL_SELECTOR = 'variant-picker, product-price, product-inventory, .buy-buttons-block';
let activeQuickAddRequestId = 0;

/**
 * Creates the compact media used by the quick-add drawer from PDP markup.
 * @param {Document | Element} source
 * @returns {HTMLElement | null}
 */
function createQuickAddMedia(source) {
  const sourceImage = source.querySelector(
    ".product-information__media slideshow-slide[aria-hidden='false'] img, .product-information__media img"
  );

  if (!(sourceImage instanceof HTMLImageElement)) return null;

  const productMedia = document.createElement('div');
  const mediaContent = /** @type {HTMLElement} */ ((sourceImage.closest('picture') || sourceImage).cloneNode(true));
  const mediaImage = mediaContent instanceof HTMLImageElement ? mediaContent : mediaContent.querySelector('img');
  const sourceGallery = sourceImage.closest('media-gallery');
  const sourceProductMedia = sourceImage.closest('.product-media');
  const sourceMediaContainer = sourceImage.closest('.product-media-container');
  const galleryAspectRatio =
    sourceGallery instanceof HTMLElement ? sourceGallery.style.getPropertyValue('--gallery-aspect-ratio').trim() : '';
  const naturalAspectRatio =
    sourceProductMedia instanceof HTMLElement ? sourceProductMedia.style.getPropertyValue('--ratio').trim() : '';
  // The gallery value can be `var(--ratio)`, but that custom property belongs
  // to the PDP media node and is not present on the compact drawer media.
  // Prefer the numeric ratio copied from the media itself.
  const mediaAspectRatio = naturalAspectRatio || galleryAspectRatio;
  const mediaFit = sourceMediaContainer?.classList.contains('media-fit-cover') ? 'cover' : 'contain';

  productMedia.className = 'product-information__media quick-add-modal__media';
  if (mediaAspectRatio) productMedia.style.setProperty('--quick-add-media-ratio', mediaAspectRatio);
  productMedia.style.setProperty('--quick-add-media-fit', mediaFit);
  mediaImage?.classList.add('quick-add-modal__media-image');
  mediaImage?.setAttribute('loading', 'eager');
  mediaImage?.setAttribute('fetchpriority', 'high');
  productMedia.appendChild(mediaContent);

  return productMedia;
}

/**
 * @param {string} productUrl
 * @returns {HTMLAnchorElement | null}
 */
function createQuickAddDetailsLink(productUrl) {
  const template = document.getElementById('quick-add-details-link-template');
  if (!(template instanceof HTMLTemplateElement)) return null;

  const detailsLink = template.content.querySelector('.quick-add-modal__details-link')?.cloneNode(true);
  if (!(detailsLink instanceof HTMLAnchorElement)) return null;

  detailsLink.href = productUrl;
  return detailsLink;
}

export class QuickAddComponent extends Component {
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  #pendingRequests = new Map();
  /** @type {Map<string, AbortController>} */
  #abortControllers = new Map();
  #isOpening = false;

  get productPageUrl() {
    const productLink = this.#getProductLink();
    const productUrl = this.dataset.productUrl || productLink?.href;
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    } else {
      // Let Shopify select the first available variant instead of retaining a sold-out variant from the card URL.
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the product link for this quick add instance.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  #getProductLink() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();
    if (productLink) return productLink;

    const resourceCard = this.closest('.resource-card');
    const resourceLink = resourceCard?.querySelector('.resource-card__link');
    if (resourceLink instanceof HTMLAnchorElement) return resourceLink;

    return null;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const resourceCard = this.closest('.resource-card');
    const selectionScope = productCard || resourceCard;
    const variantJSON = selectionScope?.querySelector(
      'swatches-variant-picker-component script[type="application/json"], variant-picker script[type="application/json"]'
    )?.textContent;

    if (variantJSON) {
      try {
        const variant = JSON.parse(variantJSON);
        if (variant?.available && variant.id) return String(variant.id);
      } catch (error) {
        console.warn('Unable to read the selected product-card variant', error);
      }
    }

    const checkedInput = selectionScope?.querySelector(
      'swatches-variant-picker-component input[type="radio"]:checked[data-variant-id]'
    );

    if (!(checkedInput instanceof HTMLInputElement)) return null;
    if (checkedInput.dataset.optionAvailable === 'false') return null;
    return checkedInput.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    for (const controller of this.#abortControllers.values()) controller.abort();
    this.#abortControllers.clear();
    this.#pendingRequests.clear();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    if (this.#isOpening) return;

    const requestId = ++activeQuickAddRequestId;
    this.#isOpening = true;
    this.toggleAttribute('loading', true);

    try {
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        await this.fetchProductPage(currentUrl);
        productGrid = this.#cachedContent.get(currentUrl);
      }

      if (productGrid) {
        if (requestId !== activeQuickAddRequestId) return;

        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        const didUpdate = await this.updateQuickAddModal(freshContent);
        if (didUpdate && requestId === activeQuickAddRequestId) this.#openQuickAddModal();
      }
    } finally {
      this.#isOpening = false;
      this.toggleAttribute('loading', false);
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    if (this.#cachedContent.has(productPageUrl)) return null;

    const pendingRequest = this.#pendingRequests.get(productPageUrl);
    if (pendingRequest) return pendingRequest;

    const controller = new AbortController();
    this.#abortControllers.set(productPageUrl, controller);

    const request = (async () => {
      const response = await fetch(productPageUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      const compactProductGrid = gridElement ? this.#createCompactProductGrid(gridElement) : null;

      if (compactProductGrid) {
        this.#cachedContent.set(productPageUrl, compactProductGrid);
      }

      return html;
    })();

    const safeRequest = request
      .catch((error) => {
        if (!(error instanceof Error) || error.name !== 'AbortError') console.error(error);
        return null;
      })
      .finally(() => {
        this.#pendingRequests.delete(productPageUrl);
        this.#abortControllers.delete(productPageUrl);
      });

    this.#pendingRequests.set(productPageUrl, safeRequest);
    return safeRequest;
  }

  /**
   * @param {Element} productGrid
   * @returns {Element | null}
   */
  #createCompactProductGrid(productGrid) {
    const compactProductGrid = /** @type {Element} */ (productGrid.cloneNode(false));
    const productDetails = productGrid.querySelector('.product-details');

    if (!productDetails) return null;

    const productMedia = createQuickAddMedia(productGrid);
    if (productMedia) {
      compactProductGrid.appendChild(productMedia);
    }

    const productHeader = document.createElement('div');
    const productTitle = document.createElement('a');
    const sourceTitle = productDetails.querySelector('.view-product-title a')?.textContent?.trim();
    productHeader.className = 'quick-add-modal__header';
    productTitle.className = 'quick-add-modal__title link';
    productTitle.textContent = this.dataset.productTitle?.trim() || sourceTitle || '';
    productTitle.href = this.productPageUrl;
    productHeader.appendChild(productTitle);
    compactProductGrid.appendChild(productHeader);

    const compactProductDetails = /** @type {Element} */ (productDetails.cloneNode(true));
    compactProductDetails.classList.remove('sticky-content--desktop', 'full-height--desktop');
    compactProductDetails.querySelector('.view-product-title')?.remove();
    const detailBlocks = compactProductDetails.querySelectorAll('.group-block-content > *');

    for (const detailBlock of detailBlocks) {
      if (!detailBlock.matches(MODAL_DETAIL_SELECTOR) && !detailBlock.querySelector(MODAL_DETAIL_SELECTOR)) {
        detailBlock.remove();
      }
    }

    const detailsLink = createQuickAddDetailsLink(this.productPageUrl);
    let appendDetailsLinkAfterProductDetails = false;
    if (detailsLink) {
      const buyButtons = compactProductDetails.querySelector('.buy-buttons-block');
      if (buyButtons) {
        buyButtons.appendChild(detailsLink);
      } else {
        appendDetailsLinkAfterProductDetails = true;
      }
    }

    compactProductGrid.appendChild(compactProductDetails);
    if (detailsLink && appendDetailsLinkAfterProductDetails) {
      compactProductGrid.appendChild(detailsLink);
    }

    return compactProductGrid;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @returns {Promise<boolean>}
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    const productDetails = productGrid.querySelector('.product-details');

    if (!productDetails) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productHeader = productGrid.querySelector('.quick-add-modal__header');
      const detailsLink = productGrid.querySelector('.quick-add-modal__details-link');

      if (productPrice) {
        productHeader?.appendChild(productPrice);
      }

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (detailsLink) {
        productGrid.appendChild(detailsLink);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    return true;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /** @param {VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const productTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-modal__title'));

    const detailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-modal__details-link'));

    if (anchorElement && productTitle) productTitle.href = anchorElement.href;
    if (anchorElement && detailsLink) detailsLink.href = anchorElement.href;

    const nextMedia = event.detail.data.html ? createQuickAddMedia(event.detail.data.html) : null;
    if (nextMedia) {
      const currentMedia = this.querySelector('.quick-add-modal__media');
      if (currentMedia) {
        currentMedia.replaceWith(nextMedia);
      } else {
        this.querySelector('.quick-add-modal__header')?.insertAdjacentElement('beforebegin', nextMedia);
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
